import { Icon, IconSize, IconVariants, useMedia } from "@next-platform/client-common-spicy-ui";
import { Chip, Image, Spacer, Spinner, Tab, Table, TableBody, TableCell, TableColumn, TableHeader, TableRow, Tabs } from "@nextui-org/react";
import { Key, useCallback, useEffect, useState } from "react";
import { useHits } from "react-instantsearch";

import { LocalizedLink } from "@/components";
import { Col, Row, Text } from "@/components/customComponents";
import Trans from "@/components/Trans/Trans";
import { formatNumber } from "@/utils";

import CreatorCard from "./CreatorCard";
import FilterByMonth from "./FilterByMonth";
import { CreatorCardDataProps, LeaderboardsTableProps } from "./types";

const TrophyLogo = "/Assets/trophy.svg";
const LeafLogo = "/Assets/leaf.svg";

function getColumns() {
  return [
    { key: "rank", label: <Trans i18nKey="leaderboard:table.column.rank" /> },
    { key: "creator_username", label: <Trans i18nKey="leaderboard:table.column.creator" /> },
    { key: "number_of_characters", label: <Trans i18nKey="leaderboard:table.column.numBots" /> },
    { key: "score", label: <Trans i18nKey="leaderboard:table.column.score" /> },
    { key: "movement", label: <Trans i18nKey="leaderboard:table.column.movement" /> },
  ];
}

export default function LeaderboardsTable(props: LeaderboardsTableProps) {
  const { setActiveTab, selectedDate, setSelectedDate } = props;
  const [top3Creators, setTop3Creators] = useState<CreatorCardDataProps[]>([]);
  const { isMobile } = useMedia();
  const [data, setData] = useState<CreatorCardDataProps[] | null>(null);
  const { hits } = useHits<CreatorCardDataProps>();
  const [months, setMonths] = useState<string[]>([]);

  useEffect(() => {
    if (hits.length) {
      if (isMobile) {
        setTop3Creators([hits[0], hits[1], hits[2]]);
      } else {
        setTop3Creators([hits[1], hits[0], hits[2]]);
      }
    }
  }, [isMobile, hits]);

  const renderStars = (rank: number) => {
    if (rank <= 3) {
      return (
        <>
          <Chip size="sm">
            <div className="space-x-1">
              <Icon icon={IconVariants.STAR} size={IconSize.XSMALL} color="yellow" />
              <Icon icon={IconVariants.STAR} size={IconSize.XSMALL} color="yellow" />
            </div>
          </Chip>
        </>
      );
    }
    if (rank <= 10) {
      return (
        <>
          <Chip size="sm">
            <Icon icon={IconVariants.STAR} size={IconSize.XSMALL} color="yellow" />
          </Chip>
        </>
      );
    }
    return <></>;
  };

  const renderCell = useCallback((creator: CreatorCardDataProps, columnKey: Key) => {
    const cellValue = creator[columnKey as keyof CreatorCardDataProps];

    switch (columnKey) {
      case "creator_username":
        return (
          <>
            <LocalizedLink to={`/creator/${creator?.creator_username}`} aria-label="creator">
              <Text className="underline" type={creator?.rank <= 10 ? "warning" : "pink-1000"}>
                {creator?.creator_username}
              </Text>
            </LocalizedLink>{" "}
          </>
        );

      case "score":
        return (
          <Row className="m-0">
            <Col>
              <Text variant="xs">{formatNumber(cellValue as number)} </Text>
            </Col>
            <Col>{renderStars(creator.rank)}</Col>
          </Row>
        );
      case "movement": {
        const movementValue = cellValue as number;
        return (
          <Text variant={"xxs"} type={movementValue > 0 ? "success" : movementValue < 0 ? "warning" : "neutral"}>
            {movementValue > 0 ? "+" : ""}
            {movementValue}
          </Text>
        );
      }
      default:
        return cellValue;
    }
  }, []);

  useEffect(() => {
    if (hits?.length) {
      const allMonths = hits.map((d) => d?.date);
      const uniqueMonths = Array.from(new Set(allMonths));

      setData(hits);
      if (!data) {
        setMonths(uniqueMonths);
      }
    }
  }, [hits]);

  if (!data) {
    return <Spinner />;
  }

  return (
    <>
      <Tabs
        aria-label="Options"
        onSelectionChange={setActiveTab}
        classNames={{
          base: "min-w-[175px]  max-w-[350px] grow",
          tabList: "dark:bg-gray-50 rounded-md w-full h-10",
          tabContent: "font-medium",
          cursor: "rounded-md",
          tab: "bg-[#00000040]",
        }}
      >
        <Tab key="New Bots" title={<Trans i18nKey="leaderboard:table.tab.newBots" />} className="!bg-transparent">
          <Text>
            <Trans i18nKey="leaderboard:table.tab.newBots.description" />
          </Text>
        </Tab>
        <Tab key="All Bots" title={<Trans i18nKey="leaderboard:table.tab.allBots" />}>
          <Text>
            <Trans i18nKey="leaderboard:table.tab.allBots.description" />
          </Text>
        </Tab>
      </Tabs>
      <Spacer y={3} />
      <Row gap={0.5}>
        <Image src={TrophyLogo} className="relative top-1" alt="" />
        <Text b type="bg-[linear-gradient(90deg,#FFDA75_0%,#998346_100%)] [-webkit-text-fill-color:transparent] bg-clip-text">
          <Trans i18nKey="leaderboard:table.freeSubscriptionBanner" />
        </Text>
      </Row>

      <Spacer y={4} />
      <Row className={"items-center mx-auto gap-8 max-[1250px]:gap-0 w-full justify-center " + (isMobile ? "pt-[1rem]" : "pt-[5rem]")}>
        {!isMobile && <Image src={LeafLogo} className="-mt-[6rem] max-[1250px]:hidden" />}
        <Row className={`max-[900px]:grow max-[900px]:justify-between ${isMobile ? "flex-col items-center gap-12" : "gap-24 max-[900px]:!gap-0"}`}>
          {top3Creators.map((d, i) => (
            <CreatorCard key={i} className={i === 1 && !isMobile ? "-mt-[5rem]" : undefined} data={d} />
          ))}
        </Row>
        {!isMobile && <Image src={LeafLogo} className="[transform:rotateY(180deg)] -mt-[6rem] max-[1250px]:hidden" />}
      </Row>
      <Spacer y={12} />
      <FilterByMonth selectedDate={selectedDate} setSelectedDate={setSelectedDate} options={months} />
      <Spacer y={4} />
      <Table isStriped aria-label="Leaderboard">
        <TableHeader>
          {getColumns().map((column) => (
            <TableColumn key={column.key} width={10} allowsSorting>
              {column.label}
            </TableColumn>
          ))}
        </TableHeader>
        <TableBody>
          {data?.map((row, index) => {
            return <TableRow key={index}>{(columnKey) => <TableCell>{renderCell(row, columnKey)}</TableCell>}</TableRow>;
          })}
        </TableBody>
      </Table>
    </>
  );
}
